import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import {
  CodingStrategyEnum,
  OrientationEnum,
  PaperFormatEnum,
  getFragmentData,
} from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useEditableTrackableData } from '../../../hooks/useEditableTrackableData.hook';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { findInitialWorkflowOfMissionWorkflow } from '../../../utils/workflows-of-mission-workflow.util';
import type { MissionReportWithItsInfo } from '../../../pages/MissionReport/types/mission-report-with-its-info.type';

import { MissionReportInfoStateForm_ResolvableMissionType_Fragment } from '../../MissionReportInfoState';
import { singleTemplateIdOf } from '../../DocumentTemplates/utils/single-template-id.util';

export const useInitialValuesFactory = (
  pInitialValues?: MissionReportWithItsInfo,
) => {
  const { generateId } = useIdFactory();

  const { trackableData } = useEditableTrackableData();

  const { mission, resolvableMissionType } = useMissionReadyContext();

  // A report being initialized takes the report template of the mission type when it has one
  // only (its report templates are suggestions).
  const missionTypeReportTemplateId = singleTemplateIdOf(
    getFragmentData(
      MissionReportInfoStateForm_ResolvableMissionType_Fragment,
      resolvableMissionType,
    )?.reportTemplateIds,
  );

  const referencesContext = useReferencesContext();

  const { defaultGenericStatus, defaultReportWorkflow, defaultReportState } =
    ensureDefined({
      defaultGenericStatus: referencesContext.defaultGenericStatus,
      defaultReportWorkflow: referencesContext.defaultReportWorkflow,
      defaultReportState: referencesContext.defaultReportState,
    });

  const {
    codingStrategiesCodeMap,
    contentOriginsCodeMap,
    documentTypesCodeMap,
    printStylesCodeMap,
  } = referencesContext;

  const initialWorkflowId = findInitialWorkflowOfMissionWorkflow(
    referencesContext.reportWorkflows,
    defaultReportWorkflow,
    mission.workflowId,
  )._id;

  const now = useMemo(
    () =>
      new Date().toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Paris',
      }),
    [],
  );

  const initialValues: MissionReportWithItsInfo = useMemo(
    () => ({
      __typename: 'Report' as const,
      _id: generateId(),
      code: `RAPPORT_${now}`,
      typeId: documentTypesCodeMap.Report._id,
      kind: documentTypesCodeMap.Report.code,
      statusId: defaultGenericStatus._id,
      title: mission.consolidation.title,
      ...trackableData,
      ...omitBy(pInitialValues, isNil),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        code: EMPTY_STRING,
        kind: CodingStrategyEnum.CodingConfigManual,
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      virtualRoot: {
        __typename: 'Section' as const,
        _id: generateId(),
        isVirtual: true,
        contentOriginId: contentOriginsCodeMap.Special._id,
        subSections: EMPTY_ARRAY,
        orientation: OrientationEnum.Portrait,
        paperFormat: PaperFormatEnum.A4,
        hasPagebreakAfter: false,
        hasPagebreakBefore: false,
        ...omitBy(pInitialValues?.virtualRoot, isNil),
      },
      printing: {
        __typename: 'ReportPrinting' as const,
        _id: generateId(),
        pdfPrints: EMPTY_ARRAY,
        pdfPrintsIncrement: 0,
        ...omitBy(pInitialValues?.printing, isNil),
        printSettings: {
          __typename: 'PrintSettings' as const,
          _id: generateId(),
          addFooters: true,
          addHeaders: true,
          printStyleId: printStylesCodeMap.Blank._id,
          ...omitBy(pInitialValues?.printing.printSettings, isNil),
        },
      },
      info: {
        __typename: 'ReportInfo' as const,
        _id: generateId(),
        reportId: EMPTY_ID,
        stateId: defaultReportState._id,
        workflowId: initialWorkflowId,
        ...omitBy(pInitialValues?.info, isNil),
      },
      // An existing report keeps its template, or none.
      templateId: pInitialValues
        ? (pInitialValues.templateId ?? EMPTY_ID)
        : (missionTypeReportTemplateId ?? EMPTY_ID),
    }),
    [
      codingStrategiesCodeMap.CodingConfigManual._id,
      contentOriginsCodeMap.Special._id,
      defaultGenericStatus._id,
      defaultReportState._id,
      initialWorkflowId,
      documentTypesCodeMap.Report._id,
      documentTypesCodeMap.Report.code,
      generateId,
      mission.consolidation.title,
      missionTypeReportTemplateId,
      now,
      pInitialValues,
      printStylesCodeMap.Blank._id,
      trackableData,
    ],
  );

  return { initialValues };
};
